import {Component, OnInit} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {Router} from '@angular/router';
import {KeycloakService} from 'keycloak-angular';

type Task = {
  phrase: string;
  routerLink: string;
  parameter: string;
  dialog?: boolean;
}

const PbmTasks: Task[] = [
  {phrase: 'Add a national drug', routerLink: '/pharmacy-management/add-national-drug', parameter: ''},
  {phrase: 'View or edit national drugs', routerLink: '/pharmacy-management', parameter: 'national-drugs'},
  {phrase: 'View or edit master drugs', routerLink: '/pharmacy-management', parameter: 'master-drugs'},
  {phrase: 'View or edit drug-to-drug interactions', routerLink: '/pharmacy-management', parameter: 'drug-interactions'},
  {phrase: 'View or edit ingredients', routerLink: '/pharmacy-management', parameter: 'ingredients'},
  {phrase: 'Add an ATC code', routerLink: '/pharmacy-management', parameter: 'atc-codes', dialog: true},
  {phrase: 'View or edit ATC codes', routerLink: '/pharmacy-management', parameter: 'atc-codes'},
  {phrase: 'Add a drug manufacturer', routerLink: '/pharmacy-management', parameter: 'manufacturers'},
  {phrase: 'View or edit drug manufacturers', routerLink: '/pharmacy-management', parameter: 'manufacturers'},
  {phrase: 'Add a master drug', routerLink: '/pharmacy-management/add-master-drug', parameter: ''},
  {phrase: 'Add a drug-to-drug interaction', routerLink: '/pharmacy-management/add-drug-interaction', parameter: ''},
  {phrase: 'Add an ingredient', routerLink: '/pharmacy-management/add-ingredient', parameter: ''},
  {phrase: 'Add a national drug formulary', routerLink: '/pharmacy-management/add-drug-formulary', parameter: ''},
  {phrase: 'View or edit national drug formularies', routerLink: '/pharmacy-management/', parameter: 'national-drug-formularies'},
  {phrase: 'Add a master drug basket', routerLink: '/pharmacy-management/add-drug-basket', parameter: ''},
  {phrase: 'Add a drug management plan', routerLink: '/pharmacy-management/add-drug-management-plan', parameter: ''},
  {phrase: 'View or edit drug management plans', routerLink: '/pharmacy-management/', parameter: 'drug-management-plans'},
  {phrase: 'View external drug data', routerLink: '/pharmacy-management/', parameter: 'external-drug-data'}
];

const EntTasks: Task[] = [
  {phrase: 'Add a provider', routerLink: '/enterprise-management/add-provider', parameter: ''},
  {phrase: 'View or edit provider', routerLink: '/enterprise-management', parameter: 'provider'},
  {phrase: 'Add a provider group', routerLink: '/enterprise-management/add-provider-group', parameter: ''},
  {phrase: 'View or edit provider group', routerLink: '/enterprise-management', parameter: 'provider-group'},
  {phrase: 'View or edit a provider price list', routerLink: '/enterprise-management', parameter: 'provider-price-list'},
  {phrase: 'Add a provider price list', routerLink: '/enterprise-management/add-provider-price-list', parameter: ''},
  {phrase: 'Add a person', routerLink: '/enterprise-management/add-person', parameter: ''},
  {phrase: 'View or edit person', routerLink: '/enterprise-management', parameter: 'person'},
  {phrase: 'View or edit policyholder group', routerLink: '/enterprise-management', parameter: 'policyholder-group'},
  {phrase: 'View or edit policy', routerLink: '/enterprise-management', parameter: 'policy'},
  {phrase: 'Add a product', routerLink: '/enterprise-management/add-product', parameter: ''},
  {phrase: 'Add a benefit', routerLink: '/enterprise-management', parameter: 'benefits', dialog: true}, // As dialog -> redirected to page and click on +Add Icon
  {phrase: 'Add a limit', routerLink: '/enterprise-management', parameter: 'limits', dialog: true},     // As dialog ->redirected to page and click on +Add Icon
  {phrase: 'View or edit products', routerLink: '/enterprise-management', parameter: 'products'},
  {phrase: 'View or edit benefits', routerLink: '/enterprise-management', parameter: 'benefits'},
  {phrase: 'View or edit limits', routerLink: '/enterprise-management', parameter: 'limits'}
];

const ClmTasks: Task[] = [
  {phrase: 'Submit or review a claim', routerLink: '/claim-management/submit-claim', parameter: ''},
  {phrase: 'Submit an authorisation', routerLink: '/claim-management/submit-authorisation', parameter: ''},
  {phrase: 'View or edit claims and authorisations', routerLink: '/claim-management', parameter: 'claims'},
  {phrase: 'Add a funeral case', routerLink: '/claim-management/add-funeral-case', parameter: ''},
  {phrase: 'View or edit funeral case', routerLink: '/claim-management', parameter: 'cases'},
  {phrase: 'Add a hospital case', routerLink: '/claim-management/add-hospital-case', parameter: ''},
  {phrase: 'View or edit hospital case', routerLink: '/claim-management', parameter: 'pharmacy'},
  {phrase: 'Add a pharmacy case', routerLink: '/claim-management/add-pharmacy-case', parameter: ''},
  {phrase: 'View or edit pharmacy case', routerLink: '/claim-management', parameter: 'pharmacy'},
  {phrase: 'Execute a payment', routerLink: '/claim-management/execute-payment', parameter: ''},
  {phrase: 'View or edit payments', routerLink: '/claim-management', parameter: 'payments'}
];

const OtherTasks: Task[] = [
  {phrase: 'View policy data', routerLink: '/data-center-management', parameter: 'data-center'},
  {phrase: 'View or edit issues', routerLink: '/issue-ticket-management', parameter: ''},
  {phrase: 'View or edit rules engine metadata', routerLink: '/rules-engine-metadata', parameter: ''},
  {phrase: 'Add user', routerLink: '/user-management', parameter: 'user-management', dialog: true},
  {phrase: 'View or edit user', routerLink: '/user-management', parameter: ''},
  {phrase: 'View support page', routerLink: '/support', parameter: ''}
];

const Tasks: Task[] = [
  ...ClmTasks,
  ...EntTasks,
  ...PbmTasks,
  ...OtherTasks
];

@Component({
  selector: 'app-home-view',
  templateUrl: './home-view.component.html',
  styleUrls: [
    './home-view.component.scss',
    '../../../../../../styles/global/_add-view-edit-styles.scss'
  ]
})
export class HomeViewComponent implements OnInit {
  tasks: string[] = [];
  userFirstName = '';
  filteredTasks: Observable<string[]>;
  taskSearchControl: FormControl = new FormControl();

  constructor(private router: Router, private keycloakService: KeycloakService) {
  }

  ngOnInit(): void {
    this.initialiseTasks();

    this.filteredTasks = this.taskSearchControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterTasksByValue(value))
    );

    this.keycloakService.loadUserProfile().then(profile => {
      this.userFirstName = profile.firstName;
    });
  }

  onSubmit(): void {
    const selectedTask: Task = Tasks.find(task => task.phrase === this.taskSearchControl.value);

    if (selectedTask) {
      const {
        routerLink,
        parameter,
        dialog
      } = selectedTask;

      const queryParams: any = {tab: parameter};

      if (dialog) {
        queryParams.dialog = dialog;
      }
      const params = parameter !== '' ? {queryParams} : undefined;

      this.router.navigate([routerLink], params);
    }
  }

  private initialiseTasks(): void {
    for (let idx = 0; idx < Tasks.length; idx++) {
      this.tasks[idx] = Tasks[idx].phrase;
    }
  }

  private filterTasksByValue(value: string): string[] {
    const filterValue = value.toLowerCase();

    if (filterValue.length === 0) {
      return [];
    }
    return this.tasks.filter(task => task.toLowerCase().includes(filterValue));
  }

}
